import {naturalSort} from '@benchling/js-utils';

import {bulkArchiveProcessManagement} from 'archive/ArchiveActions';
import {ArchiveModalItem, open as openArchiveModal} from 'archive/components/modals/ArchiveModal';
import BenchlingConfig from 'BenchlingConfig';
import {BulkArchiveResponse} from 'processes/dao/ProcessManagementDAO';
import {ProcessWithStatusCounts} from 'processes/dashboard/ProcessTaskStatusCounts';
import CannotArchiveProcessItemsModal from 'processes/modals/CannotArchiveProcessItemsModal';
import {ProcessManagementItemType} from 'processes/ProcessConstants';
import {isTerminalStatus} from 'processes/status/TaskStatusUtil';
import runModal from 'ui-library/runModal';

import {ProcessTaskStatusLifecycleGraphQL as ProcessTaskStatusLifecycle} from '../../../graphql-fragments/processes/__generated/ProcessTaskStatusLifecycleGraphQL';

interface ArchivableProcess extends ArchiveModalItem, ProcessWithStatusCounts {
  __typename: 'ProcessNode';
  id: string;
  processTaskSchema: {
    __typename: 'ProcessTaskSchemaNode';
    statusLifecycle: ProcessTaskStatusLifecycle;
  };
}

interface ArchivableProcessTask extends ArchiveModalItem {
  __typename: 'ProcessTaskNode';
  id: string;
  schema: {
    __typename: 'ProcessTaskSchemaNode';
    statusLifecycle: ProcessTaskStatusLifecycle;
  };
  status: {
    id: string;
    displayName: string;
  };
}

interface ArchivableProcessOutput extends ArchiveModalItem {
  __typename: 'ProcessOutputNode';
  id: string;
}

type ArchivableProcessItem = ArchivableProcess | ArchivableProcessTask | ArchivableProcessOutput;

function isArchivableProcess(item: ArchivableProcessItem): item is ArchivableProcess {
  return item.__typename === 'ProcessNode';
}

function isArchivableProcessTask(item: ArchivableProcessItem): item is ArchivableProcessTask {
  return item.__typename === 'ProcessTaskNode';
}

export function getTerminalStatusNames(item: ArchivableProcessItem): Array<string> {
  let transitions;
  let statusLifecycle: ProcessTaskStatusLifecycle;

  if (isArchivableProcess(item)) {
    transitions = item.processTaskSchema.statusLifecycle.transitions;
    statusLifecycle = item.processTaskSchema.statusLifecycle;
  } else if (isArchivableProcessTask(item)) {
    transitions = item.schema.statusLifecycle.transitions;
    statusLifecycle = item.schema.statusLifecycle;
  } else {
    return [];
  }

  return transitions
    .filter((transition) => isTerminalStatus(transition.outStatus.id, statusLifecycle))
    .map((transition) => transition.outStatus.displayName);
}

export function openArchiveModalForProcessItems(
  itemType: ProcessManagementItemType,
  items: Array<ArchivableProcessItem>,
  onArchived?: (archiveResponse: BulkArchiveResponse | void) => void
): void {
  if (BenchlingConfig.WORKFLOWS_ENABLE_ARCHIVE_VALIDATION && !canArchiveProcessItems(items)) {
    openCannotArchiveProcessItemsModal(items);
  } else {
    openArchiveModal({
      items,
      onArchive: (itemIds: Array<string>, purpose: string) => {
        bulkArchiveProcessManagement(itemType, itemIds, purpose).then(
          (archiveResponse: BulkArchiveResponse | void) => {
            if (onArchived && archiveResponse != null) {
              onArchived(archiveResponse);
            }
          }
        );
      },
    });
  }
}

export function openCannotArchiveProcessItemsModal(items: Array<ArchivableProcessItem>): void {
  const terminalStatuses = new Set<string>();

  // Collect terminal status names from all items
  for (const item of items) {
    for (const status of getTerminalStatusNames(item)) {
      terminalStatuses.add(status);
    }
  }

  const uniqueTerminalStatuses = naturalSort([...new Set(terminalStatuses)]);

  runModal(CannotArchiveProcessItemsModal, {
    title: `Task${items.length > 1 ? 's' : ''} can't be archived`,
    body: `One or more tasks can't be archived until they have a terminal status (i.e. ${uniqueTerminalStatuses.join(', ')}).`,
  });
}

export function canArchiveProcessItems(items: Array<ArchivableProcessItem>): boolean {
  for (const item of items) {
    let allStatusesTerminal = true;

    // If we are archiving a root/task process, and it's just been cancelled, it's
    // possible that the child tasks are not yet terminal. That will happen when our
    // propagate_flowchart_cancellation_to_descendant_tasks celery task completes.
    // If we run into this race condition, we will return true. The user can still archive
    // the root task, and the child tasks will be archived in a nonterminal state. However,
    // the propagate_flowchart_cancellation_to_descendant_tasks celery task will still eventually
    // terminate them.

    if (isArchivableProcess(item)) {
      // get all task statuses with count > 0, and check if all are terminal
      const statuses = item.taskStatusCounts.filter((status) => status.count > 0);
      allStatusesTerminal = statuses.every((status) =>
        isTerminalStatus(status.status.id, item.processTaskSchema.statusLifecycle)
      );
    } else if (isArchivableProcessTask(item)) {
      allStatusesTerminal = isTerminalStatus(item.status.id, item.schema.statusLifecycle);
    }

    if (!allStatusesTerminal) {
      return false;
    }
  }

  return true;
}
